import type {SearchParams} from '@/modules/common/context/QueryParams/models';
import type {Nullable} from '@/types';
import type {SearchFormValues} from '@travel-frontend/ui-components';

import {Room} from '@/modules/common/models/room';
import {Rooms} from '@/modules/common/models/rooms';

type ReturnType = Pick<
  SearchFormValues,
  'airports' | 'travelPeriod' | 'travellers' | 'travellersMultiroom'
>;

export const mapSearchParamsToFormValues = ({
  dateMin,
  dateMax,
  durationMin,
  durationMax,
  airports,
  rooms,
}: SearchParams): ReturnType => {
  const formValues: ReturnType = {
    airports: null,
    travelPeriod: null,
    travellers: null,
    travellersMultiroom: null,
  };

  if (airports) {
    formValues.airports = airports;
  }

  if (dateMin && dateMax) {
    formValues.travelPeriod = {
      startDate: dateMin,
      endDate: dateMax,
      duration:
        durationMin != null && durationMax != null
          ? {min: durationMin, max: durationMax}
          : null,
    };
  }

  formValues.travellersMultiroom = {
    adultsCount: rooms?.adultsCount ?? 0,
    childrenCount: rooms?.childrenCount ?? 0,
    rooms: Array.from(rooms ?? [], (room) => ({
      adultsCount: room.adultsCount,
      childrenAges: room.childrenAges,
    })),
  };

  return formValues;
};

export const mapFormValuesToSearchParams = (
  formValues: SearchFormValues
): Nullable<SearchParams> => {
  const searchParams: Nullable<SearchParams> = {
    destinationId: null,
    dateMin: null,
    dateMax: null,
    airports: null,
    adults: null,
    children: null,
    durationMin: null,
    durationMax: null,
    rooms: null,
    travelType: null,
  };

  if (formValues.destination?.id) {
    searchParams.destinationId = formValues.destination.id;
  }
  if (formValues.airports) {
    searchParams.airports = formValues.airports;
  }
  if (formValues.travelPeriod?.startDate) {
    searchParams.dateMin = formValues.travelPeriod.startDate;
  }
  if (formValues.travelPeriod?.endDate) {
    searchParams.dateMax = formValues.travelPeriod.endDate;
  }
  if (formValues.travelPeriod?.duration) {
    searchParams.durationMin = formValues.travelPeriod.duration.min;
    searchParams.durationMax = formValues.travelPeriod.duration.max;
  }

  if (formValues.travellersMultiroom?.adultsCount) {
    searchParams.rooms = Rooms.create(
      ...formValues.travellersMultiroom.rooms.map((room) =>
        Room.create({
          adultsCount: room.adultsCount,
          childrenAges: room.childrenAges.filter(
            (childrenAge): childrenAge is number => childrenAge !== null
          ),
        })
      )
    );
  }

  return searchParams;
};
